import { Routes } from '@angular/router';
import { DashboardLayout } from './layouts/dashboard/dashboard.layout';
import { UserListPage } from './features/user/components/user-list/user-list.component';
import { AuthenticationLayout } from './layouts/authentication/authentication.layout';
import { SignInComponent } from './layouts/authentication/components/sign-in/sign-in.component';
import { OrganizationListPage } from './features/organization/components/organization-list/organization-list.component';
import { MemberListPage } from './features/member/components/member-list/member-list.component';
import { PropertyListPage } from './features/property/components/property-list/property-list.component';
import { UserProfilePage } from './features/user/pages/user-profile/user-profile.page';
import { StaffManagementPage } from './features/staff-management/pages/staff-management/staff-management.page';
import { StaffRoleConstants } from './core/constants/staff-roles.constants';
import { PropertyProfilePage } from './features/property/pages/property-profile/property-profile.page';
import { AuthGuard } from '@auth0/auth0-angular';
import { AuthForwarderComponent } from './layouts/authentication/components/auth-forwarder/auth-forwarder.component';
import { organizationRequiredGuard } from './core/routing/organization-required-guard';
import { organizationQueryParamGuard } from './core/routing/organization-query-param.guard';
import { MemberProfilePage } from './features/member/pages/member-profile/member-profile.page';
import { permissionsGuard } from './core/routing/permissions.guard';
import { featureFlagGuard } from './core/routing/feature-flag.guard';
import { maintenanceLandingGuard } from './core/routing/maintenance-landing.guard';
import { authCallbackErrorGuard } from './core/routing/auth-callback-error.guard';
import { AuthCallbackConstants } from './core/constants/constants';
import { canDeactivateGuard } from './core/routing/can-deactivate.guard';
import { FeatureFlag } from './core/enums/feature-flags.enums';
import { BalancesPermission, BankReconciliationPermission, ChartOfAccountsPermission, FeatureFlagPermission, FinancialsPermission, ImportPermission, MemberPermission, MessagePermission, OrganizationPermission, PaymentsPermission, PropertyPermission, SupportPermission, StripePermission, UserPermission } from './core/enums/permissions.enums';
import { userLoadedGuard } from './core/routing/user-loaded.guard';
import { OrganizationProfilePage } from './features/organization/pages/organization-profile/organization-profile.page';
import { SystemPage } from './layouts/system-page/system.page';
import { AcceptInvitationComponent } from './layouts/authentication/components/accept-invitation/accept-invitation.component';
import { ChargeScheduleForm } from './forms/charge-schedule/charge-schedule.form';
import { ExpenseForm } from './forms/expense/expense.form';
import { PropertyChargeScheduleListComponent } from './features/balances/components/property-charge-schedule-list/property-charge-schedule-list.component';
import { PropertyChargeScheduleSummaryPage } from './features/balances/pages/property-charge-schedule-summary/property-charge-schedule-summary.page';
import { PropertyChargeScheduleEditPage } from './features/balances/pages/property-charge-schedule-edit/property-charge-schedule-edit.page';
import { InvoiceDashboardPage } from './features/balances/pages/invoice-dashboard/invoice-dashboard.page';
import { softDeleteGuard, SoftDeleteEntityType } from './core/routing/soft-deleted-entity.guard';
import { DashboardPage } from './features/dashboard/pages/dashboard/dashboard.page';
import { ManualGLEntriesPage } from './features/general-ledger/pages/manual-gl-entries/manual-gl-entries.page';
import { TrialBalancePage } from './features/reports/pages/trial-balance/trial-balance.page';
import { BalanceSheetPage } from './features/reports/pages/balance-sheet/balance-sheet.page';
import { IncomeStatementPage } from './features/reports/pages/income-statement/income-statement.page';
import { ReportsLayoutPage } from './features/reports/pages/reports-layout/reports-layout.page';
import { BudgetEntryPage } from './features/budgets/pages/budget-entry/budget-entry.page';
import { BudgetSelectionPage } from './features/budgets/pages/budget-selection/budget-selection.page';
import { BudgetSummaryPage } from './features/budgets/pages/budget-summary/budget-summary.page';
import { PaymentHistoryPage } from './features/balances/pages/payment-history/payment-history.page';
import { ExpenseListPage } from './features/expense/pages/expense-list/expense-list.page';
import { DocumentsPage } from './features/documents/pages/documents.page';
import { SentEmailListPage } from './features/sent-email/pages/sent-email-list/sent-email-list.page';
import { CommunicationsPage } from './features/communications/pages/communications/communications.page';
import { ContactSupportPage } from './features/support/pages/contact-support/contact-support.page';
import { ChartOfAccountsPage } from './features/chart-of-accounts/pages/chart-of-accounts/chart-of-accounts.page';
import { MemberDirectoryPage } from './features/member/pages/member-directory/member-directory.page';

export const routes: Routes = [
  {
    title: "HOA Start", path: "", pathMatch: "full",
    canActivate: [maintenanceLandingGuard], children: [],
  },
  {
    title: "HOA Start", path: "", component: DashboardLayout, canActivate: [AuthGuard, userLoadedGuard],
      children: [
        { title: "Dashboard", path: "dashboard", component: DashboardPage,
          canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Dashboard'} },

        { title: "Users", path: "users", component: UserListPage,
          canActivate: [permissionsGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Users', permissions: [UserPermission.Read]} },

        // Anyone who can grant ANY staff role may open the page, so the gate is the union of the
        // table's permissions with requireAll: false — derived rather than hand-listed, because a
        // hand-listed pair is both redundant (a System Administrator's roles.* already matches
        // roles.staff) and liable to drift, as it had: roles.sys_admin became manageable and the
        // list did not follow. userPermissionOnly matches the sidebar gate — these are platform
        // grants, so an organization-scoped one must never open this page.
        { title: "Staff Management", path: "staff-management", component: StaffManagementPage,
          canActivate: [permissionsGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Staff Management', permissions: StaffRoleConstants.MANAGEABLE().map(manageable => manageable.permission),
          requireAll: false, userPermissionOnly: true} },

        { title: "Organizations", path: "organizations", component: OrganizationListPage,
          canActivate: [permissionsGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Organizations', permissions: [OrganizationPermission.Read]} },

        // Platform administration, not organization work: no organizationRequiredGuard, and
        // userPermissionOnly so only a user-level grant of feature_flags.* opens it. Lazy-loaded for
        // the same budget reason as Bank Reconciliation below — nobody but staff ever opens it.
        { title: "Feature Flags", path: "feature-flags",
          loadComponent: () => import('./features/feature-flags/pages/feature-flags/feature-flags.page')
            .then(m => m.FeatureFlagsPage),
          canActivate: [permissionsGuard],
          data: {breadcrumb: 'Feature Flags', permissions: [FeatureFlagPermission.Read], userPermissionOnly: true} },

        { title: "Members", path: "members", component: MemberListPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Members', permissions: [MemberPermission.Read]} },

        // No permissionsGuard: the directory exists for Members, who hold no permissions at all, so
        // any members.* gate would lock out its entire audience. Same reasoning as Documents below.
        { title: "Member Directory", path: "member-directory", component: MemberDirectoryPage,
          canActivate: [organizationRequiredGuard],
          data: {breadcrumb: 'Member Directory'} },

        { title: "Properties", path: "properties", component: PropertyListPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Properties', permissions: [PropertyPermission.Read]}},

        // Lazy for the same bundle-budget reason as Feature Flags above; the wizard tickets hang
        // their steps off this page.
        { title: "Import", path: "import",
          loadComponent: () => import('./features/import/pages/import-landing/import-landing.page')
            .then(m => m.ImportLandingPage),
          canActivate: [permissionsGuard, organizationRequiredGuard, featureFlagGuard],
          data: {breadcrumb: 'Import', permissions: [ImportPermission.Manage],
                 featureFlag: FeatureFlag.MemberPropertyImport} },

        { title: "Organization Profile", path: "organizations/profile/:organizationId", component: OrganizationProfilePage,
           canActivate: [permissionsGuard, softDeleteGuard], canDeactivate: [canDeactivateGuard],
           data: {breadcrumb: 'Organization Profile', permissions: [OrganizationPermission.Read],
           softDeleteCheck: { paramKey: 'organizationId', entityType: SoftDeleteEntityType.Organization }} },

        { title: "Member Profile", path: "members/profile/:memberId", component: MemberProfilePage,
           canActivate: [permissionsGuard, softDeleteGuard], canDeactivate: [canDeactivateGuard],
           data: {breadcrumb: 'Member Profile',
           softDeleteCheck: { paramKey: 'memberId', entityType: SoftDeleteEntityType.Member }} },

        { title: "User Profile", path: "users/profile/:userId", component: UserProfilePage,
           canActivate: [permissionsGuard, softDeleteGuard], canDeactivate: [canDeactivateGuard],
           data: {breadcrumb: 'User Profile',
           softDeleteCheck: { paramKey: 'userId', entityType: SoftDeleteEntityType.User }} },

        { title: "Property Profile", path: "properties/profile/:propertyId", component: PropertyProfilePage,
           canActivate: [permissionsGuard, softDeleteGuard], canDeactivate: [canDeactivateGuard],
           data: {breadcrumb: 'Property Profile',
           softDeleteCheck: { paramKey: 'propertyId', entityType: SoftDeleteEntityType.Property }} },

        { title: "Create Invoice Schedule", path: "invoice-schedules/create", component: ChargeScheduleForm,
          canActivate: [permissionsGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Create Invoice Schedule', permissions: [BalancesPermission.CreateChargeSchedules]} },

        { title: "Edit Invoice Schedule", path: "invoice-schedules/edit/:propertyChargeScheduleId", component: PropertyChargeScheduleEditPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Edit Invoice Schedule', permissions: [BalancesPermission.Update]} },

        { title: "Invoice Schedules", path: "invoice-schedules", component: PropertyChargeScheduleListComponent,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Invoice Schedules', permissions: [BalancesPermission.Read]} },

        { title: "Invoice Dashboard", path: "invoices/dashboard", component: InvoiceDashboardPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Invoice Dashboard', permissions: [BalancesPermission.Read]} },

        { title: "Invoice Schedule Summary", path: "invoice-schedules/summary/:propertyChargeScheduleId", component: PropertyChargeScheduleSummaryPage,
          canActivate: [permissionsGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Invoice Schedule Summary', permissions: [BalancesPermission.Read]} },

        // Org-notice emails link here with ?organizationId= (DEV-2044). Guards on one route run concurrently,
        // so the switch sits on this componentless parent: it finishes before the child's guards start, and
        // they then check the organization that is actually active.
        { path: "payment-dashboard", canActivate: [organizationQueryParamGuard],
          children: [
            { title: "Payment Dashboard", path: "", component: PaymentHistoryPage,
              canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
              data: {breadcrumb: 'Payment Dashboard', permissions: [PaymentsPermission.Read]} },
          ]
        },

        // Keeps links bookmarked before the Payment Dashboard rename off the 404 wildcard.
        { path: "payment-history", redirectTo: "payment-dashboard", pathMatch: "full" },

        { title: "Create Expense", path: "expense/create", component: ExpenseForm,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Create Expense', permissions: [FinancialsPermission.CreateExpense]} },

        { title: "Expenses", path: "expenses", component: ExpenseListPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Expenses', permissions: [FinancialsPermission.Read]} },

        { title: "Email History", path: "sent-emails", component: SentEmailListPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Email History', permissions: [MessagePermission.Read]} },

        { title: "Send Email", path: "send-email", component: CommunicationsPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Send Email', permissions: [MessagePermission.Send]} },

        { title: "Contact Support", path: "support", component: ContactSupportPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Contact Support', permissions: [SupportPermission.CreateTicket]} },

        { title: "Documents", path: "documents", component: DocumentsPage,
          canActivate: [organizationRequiredGuard],
          canDeactivate: [canDeactivateGuard],
          // No documents permission required: any authenticated org member may browse (so no
          // permissionsGuard). The API scopes which folders/files are returned and authorizes downloads.
          data: {breadcrumb: 'Documents'} },

        { title: "Manual GL Entries", path: "manual-gl-entries", component: ManualGLEntriesPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Manual GL Entries', permissions: [FinancialsPermission.CreateLedgerEntry]} },

        { title: "Chart of Accounts", path: "chart-of-accounts", component: ChartOfAccountsPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Chart of Accounts', permissions: [ChartOfAccountsPermission.Read]} },

        // Both permissions, because the page reads from two controllers that gate differently:
        // bank-connections/filter enforces stripe.read, while the summary and bank-transactions/filter
        // enforce bank_reconciliation.read. The guard requires all listed permissions by default,
        // which is what this page needs to function rather than half-load.
        //
        // Connecting an account additionally needs stripe.connect_bank, checked in the page.
        //
        // Lazy-loaded rather than imported eagerly like the routes above: the initial bundle sits
        // within ~60 kB of its 7 MB warning budget, and this route pulls in the Stripe Financial
        // Connections path that most users never open.
        { title: "Bank Reconciliation", path: "bank-reconciliation",
          loadComponent: () => import('./features/bank-reconciliation/pages/bank-reconciliation/bank-reconciliation.page')
            .then(m => m.BankReconciliationPage),
          canActivate: [permissionsGuard, organizationRequiredGuard, featureFlagGuard],
          data: {breadcrumb: 'Bank Reconciliation',
                 permissions: [StripePermission.Read, BankReconciliationPermission.Read],
                 featureFlag: FeatureFlag.BankReconciliation} },

        { title: "Create Budget", path: "budgets/create", component: BudgetEntryPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Create Budget', permissions: [FinancialsPermission.CreateBudget]} },

        { title: "Edit Budget", path: "budgets/:budgetId/edit", component: BudgetEntryPage,
          canActivate: [permissionsGuard, organizationRequiredGuard], canDeactivate: [canDeactivateGuard],
          data: {breadcrumb: 'Edit Budget', permissions: [FinancialsPermission.UpdateBudget]} },

        { title: "Budget Summary", path: "budgets/:budgetId", component: BudgetSummaryPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Budget Summary', permissions: [FinancialsPermission.Read]} },

        { title: "Budgets", path: "budgets", component: BudgetSelectionPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Budgets', permissions: [FinancialsPermission.Read]} },

        { title: "Financial Reports", path: "reports", component: ReportsLayoutPage,
          canActivate: [permissionsGuard, organizationRequiredGuard],
          data: {breadcrumb: 'Reports', permissions: [FinancialsPermission.Read]},
          children: [
            { path: "", pathMatch: "full", redirectTo: "trial-balance" },
            { title: "Trial Balance", path: "trial-balance", component: TrialBalancePage,
              data: {breadcrumb: 'Trial Balance'} },
            { title: "Balance Sheet", path: "balance-sheet", component: BalanceSheetPage,
              data: {breadcrumb: 'Balance Sheet'} },
            { title: "Income Statement", path: "income-statement", component: IncomeStatementPage,
              data: {breadcrumb: 'Income Statement'} }
          ]
        },
      ]
  },
  {
    title: "HOA Start", path: "", component: AuthenticationLayout,
      children: [
        { title: "HOA Start - Sign In", path: "sign-in", component: SignInComponent},
        { title: "HOA Start - Accept Invitation", path: "invite", component: AcceptInvitationComponent},
      ]
  },
  { 
    title: "HOA Start - Authentication", path: "authenticate", component: AuthForwarderComponent
  },
  // Unguarded on purpose: whoever lands here just failed to sign in. The guard always redirects.
  {
    title: "HOA Start - Authentication", path: AuthCallbackConstants.ERROR_PATH(),
    canActivate: [authCallbackErrorGuard], children: [],
  },
  { 
    title: "HOA Start - System", path: "system/:messageId", component: SystemPage
  },
  //THIS NEEDS TO BE AT THE BOTTOM OF THE ROUTES ARRAY
  { 
    path: "**", redirectTo:"system/404"
  },
];
